<template>
  <div class="pages-split-view" @contextmenu="onContextMenu">
    <div class="pages-left-pane">
      <BaseResizableLeftPane
        storage-key="pages/panes/left:width"
        :is-resizable="true"
        :min-width="252"
        :max-width="400"
        @resized="resize"
      >
        <div class="pages-resizable-content">
          <BasePaneHeader :is-hideable="false" compact>
            <template v-if="$slots['header-right']" #right>
              <slot name="header-right" />
            </template>
          </BasePaneHeader>
          <div v-if="$slots.toolbar" class="pages-toolbar">
            <slot name="toolbar" />
          </div>
          <div class="pages-list">
            <slot v-if="isListLoading" name="loading" />
            <slot v-else name="list" />
          </div>
        </div>
      </BaseResizableLeftPane>
    </div>
    <div class="pages-preview">
      <slot name="preview" :pages-pane-width="pagesPaneWidth" />
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref } from "vue";

import BasePaneHeader from "@/components/base/BasePaneHeader.vue";
import BaseResizableLeftPane from "@/components/base/BaseResizableLeftPane.vue";
import * as inject from "@/components/inject";

export default defineComponent({
  name: "PagesSplitView",
  components: {
    BasePaneHeader,
    BaseResizableLeftPane,
  },
  props: {
    isListLoading: { type: Boolean, default: false },
    preventContextMenu: { type: Boolean, default: false },
  },
  emits: {
    resized: (_width: number) => true,
  },
  setup(props, { emit }) {
    const pagesPaneWidth = ref(252);
    inject.pagesPaneWidth(pagesPaneWidth);

    const resize = (width: number) => {
      pagesPaneWidth.value = width;
      emit("resized", width);
    };
    const onContextMenu = (event: MouseEvent) => {
      if (props.preventContextMenu) event.preventDefault();
    };

    return {
      onContextMenu,
      pagesPaneWidth,
      resize,
    };
  },
});
</script>

<style scoped lang="scss">
.pages-split-view {
  display: flex;
  flex-direction: row;
  width: 100%;
  height: 100%;
}

.pages-left-pane {
  .pages-resizable-content {
    display: flex;
    flex-direction: column;
    width: 100%;
    max-width: 100%;

    .pages-list {
      display: flex;
      flex-direction: column;
      height: 100%;
      padding: 12px 8px 16px;
      overflow-y: auto;
    }
  }
}

.pages-preview {
  flex: 1;
  min-width: 0;
  height: 100%;

  // Bound the custom-object preview's height so it scrolls internally, like
  // the full-screen CustomObject route bounds it through flex stretching:
  // left unbounded it grows with the object's content, the whole split view
  // overflows and scrolls as a document, and the left pane's divider border
  // stops at the viewport instead of running to the bottom — the same bug
  // PersonalPagePreview's own height fixes for the page preview.
  :deep(> .object) {
    height: 100%;
    box-sizing: border-box;
  }

  :deep(.loading) {
    box-sizing: border-box;
    max-width: $document-max-width;
    padding: 76px 0 0 100px;
  }
}
</style>
